import { type ChangeEvent, type MouseEvent, useCallback, useState, forwardRef } from 'react';
import { css, useTheme } from '@emotion/react';
import { Eye } from '@phosphor-icons/react';
import { Button } from 'client/components/Button';
import { Tooltip } from 'client/components/Tooltip';
import { borderRadius } from 'client/Theme';
import { Input, type InputProps } from './Input';

interface RevealIconProps {
  revealed: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

const RevealIcon = ({ revealed, onClick }: RevealIconProps) => {
  const theme = useTheme();
  return (
    <Tooltip side="left" title={revealed ? 'Hide the secret value' : 'Show the secret value'}>
      <Button aria-label="toggle secret value visibility" onClick={onClick}>
        <Eye size={24} weight={revealed ? 'fill' : 'regular'} color={revealed ? theme.color.primary[100] : theme.color.grey[100]} />
      </Button>
    </Tooltip>
  );
};

interface AdornmentProps {
  id: string;
  type: 'password' | string;
  revealed: boolean;
  onRevealedToggle: () => void;
}

const Adornment = ({ type, revealed, onRevealedToggle }: AdornmentProps) => {
  const items = [];
  if (type === 'password') items.push(<RevealIcon key="reveal" revealed={revealed} onClick={onRevealedToggle} />);

  if (!items.length) return null;

  return <div css={adornmentStyle}>{items}</div>;
};

export interface ConfigurationInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  id: string;
  value?: string | number | boolean | null;
  onChange?: (value?: string | number | boolean | null) => void;
}

export const ConfigurationInput = forwardRef<HTMLInputElement, ConfigurationInputProps>(({ id, value, onChange, ...props }, ref) => {
  const [secretVisible, setSecretVisible] = useState(false);

  const handleRevealedToggle = useCallback(() => setSecretVisible((state) => !state), []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!onChange) return;
      const value = event.target.value;
      onChange(value);
    },
    [onChange]
  );

  const isSecret = props.type === 'password';

  return (
    <fieldset css={containerStyle}>
      <Input
        name={id}
        value={`${value}`}
        onChange={handleChange}
        {...props}
        type={isSecret && secretVisible ? 'text' : props.type}
        ref={ref}
      />
      <Adornment id={id} type={props.type || ''} revealed={secretVisible} onRevealedToggle={handleRevealedToggle} />
    </fieldset>
  );
});

const containerStyle = css`
  display: flex;
  gap: 2px;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: min(100%, 65ch);
  border: none;

  > .input {
    flex: 1;

    input,
    textarea {
      margin: 0;
      box-sizing: border-box;
    }

    &:not(:last-child) input {
      border-radius: ${borderRadius}px 0 0 ${borderRadius}px;
    }
  }
`;

const adornmentStyle = css`
  display: flex;

  > button {
    flex-shrink: 0;
    padding: 0;
    min-height: 56px;
    width: 56px;
    border-radius: 0;

    &:last-of-type {
      border-radius: 0 ${borderRadius}px ${borderRadius}px 0;
    }

    > span {
      display: flex;
      margin: 0;
    }
  }
`;
